











































































































































































import { Component, Mixins, Ref, Watch } from "vue-property-decorator";
import DatePicker, {
  DATE_FORMAT,
  DatePickerData,
} from "@/components/DatePicker.vue";
import AlertFeedList from "@/components/AlertFeedList.vue";
import ComparisonGroupSelect from "@/components/ComparisonGroupSelect.vue";
import { Getter } from "vuex-class";
import ReportBroadcasterSelect from "@/components/Report/BroadcasterSelect.vue";

import InvestorTopList, {
  TopListFilter,
} from "@/components/Investor/TopList.vue";
import InvestorComparatorChart, {
  ComparatorChartFilter,
} from "@/components/Investor/ComparatorChart.vue";
import { Company, ComparisonGroup, Target, User } from "@/models";
import MediaValue from "@/interfaces/MediaValue";
import QueryHelper from "@/services/QueryHelper";
import { COMPANY_TYPE } from "@/models/CompanyType";
import GrpTargetSelect from "@/components/GrpTargetSelect.vue";
import GrpTargetMixin from "@/mixins/http/Grp/Target";
import GrpDisclaimer from "@/components/GrpTargets/Disclaimer.vue";

interface DashboardFilter {
  group: ComparisonGroup | null;
  date: {
    from: string;
    to: string;
  };
  grpTarget: string | null;
}

Component.registerHooks(["beforeRouteUpdate"]);

@Component({
  components: {
    GrpTargetSelect,
    DatePicker,
    AlertFeedList,
    ComparisonGroupSelect,
    InvestorTopList,
    InvestorComparatorChart,
    GrpDisclaimer,
    ReportBroadcasterSelect,
  },
})
export default class Dashboard extends Mixins(GrpTargetMixin) {
  @Getter("locked") readonly locked!: boolean;
  @Getter("lastDate") readonly lastDate!: string;
  @Getter("userMedia") readonly userMedia!: MediaValue;
  @Getter("user") readonly user!: User;
  @Getter readonly parseAndCleanMediaValue!: (
    radio: string | boolean | undefined,
    tv: string | boolean | undefined
  ) => MediaValue;
  @Getter readonly parseDateValue!: (
    date: string,
    format: string,
    defaultOffset?: number,
    defaultUnit?: any
  ) => string;

  @Ref() readonly targetSelector!: GrpTargetSelect;

  filter: DashboardFilter = {
    group: null,
    date: { from: "", to: "" },
    grpTarget: null,
  };

  currentGrpTarget: Target | null = null;

  topListOverItem: Company | undefined = new Company({});
  groupReady = false;

  async created() {
    if (this.isAdSalesAgency && !this.isAdmin) {
      this.$router.push({ name: "SpecialInitiativesRoot" });
      return;
    }

    if (this.user.isAgencyUser && !this.isAdmin) {
      this.$router.push({ name: "competitor-report" });
      return;
    }

    this.filter = this.buildFilterFromQuery();
    this.filter.grpTarget = this.currentGrpTarget?.id;
  }

  get isAdSalesAgency(): boolean {
    if (!this.user) {
      return false;
    }

    return (
      this.user.profile?.company.companyType === COMPANY_TYPE.AD_SALES_AGENCY ||
      this.user.profile?.company.companyType === COMPANY_TYPE.TV_AD_SALES_AGENCY
    );
  }

  get isAdmin(): boolean {
    if (!this.user) {
      return false;
    }

    return this.user.isAdmin;
  }

  get rightTopListTitle() {
    if (this.user.hasFeature("auditel")) {
      return `TV (GRP - target ${this.currentGrpTarget?.name ?? "N/D"} )`;
    } else {
      return (this.userMedia.tv ? "TV" : "RADIO") + " (secondi)";
    }
  }

  get rightTopListType() {
    return this.user.hasFeature("auditel") ? "grp" : "seconds";
  }

  get targetId() {
    return this.filter.grpTarget;
  }

  async onTargetSelected(target: Target) {
    this.currentGrpTarget = target;
    this.filter.grpTarget = this.currentGrpTarget?.id;
  }

  buildFilterFromQuery(): DashboardFilter {
    const query = this.$route.query;

    const from = this.parseDateValue(
      query.from as string,
      DATE_FORMAT,
      6,
      "days"
    );
    const to = this.parseDateValue(query.to as string, DATE_FORMAT);
    const date = { from, to };
    const grpTarget = {
      grpTarget: (query.grpTarget ?? query.target) as string,
    };
    return { date, ...grpTarget, group: null };
  }

  @Watch("filter", { deep: true })
  onFilterChanged() {
    if (!QueryHelper.isSameQuery(this.$route.query, this.queryFilter)) {
      this.$router.replace({ query: { ...this.queryFilter } });
    }
  }

  get queryFilter(): any {
    return {
      from: this.filter?.date?.from,
      to: this.filter?.date?.to,
      grpTarget: this.filter?.grpTarget ?? undefined,
    };
  }

  get periodDescription(): string {
    const start = this.$moment(this.filter?.date.from);
    const end = this.$moment(this.filter?.date.to);
    const days = end.diff(start, "days") + 1;

    return `Ultimi ${days} giorni`;
  }

  get leftTopListFilter(): TopListFilter {
    const media = {
      radio: true,
      tv: false,
    };

    if (this.userMedia.tv && this.userMedia.radio) {
      media.radio = true;
      media.tv = false;
    } else if (this.userMedia.tv) {
      media.radio = false;
      media.tv = true;
    }

    return {
      media,
      taxonomy: {},
      groupBy: "companies",
      grpTarget: null,
      date: {
        from:
          this.filter?.date.from ||
          this.$moment(this.lastDate, DATE_FORMAT)
            .subtract(6, "days")
            .format(DATE_FORMAT),
        to:
          this.filter?.date.to ||
          this.$moment(this.lastDate, DATE_FORMAT).format(DATE_FORMAT),
      },
      compare: {
        from: this.$moment(this.filter?.date.from, DATE_FORMAT)
          .subtract(1, "years")
          .format(DATE_FORMAT),
        to: this.$moment(this.filter?.date.to, DATE_FORMAT)
          .subtract(1, "years")
          .format(DATE_FORMAT),
      },
    };
  }

  get rightTopListFilter(): TopListFilter {
    const media = {
      radio: false,
      tv: true,
    };

    if (this.userMedia.tv && this.userMedia.radio) {
      media.radio = false;
      media.tv = true;
    } else if (this.userMedia.radio) {
      media.radio = true;
      media.tv = false;
    }

    return {
      media,
      taxonomy: {},
      groupBy: "companies",
      grpTarget: this.filter?.grpTarget,
      date: {
        from:
          this.filter?.date.from ||
          this.$moment(this.lastDate, DATE_FORMAT)
            .subtract(6, "days")
            .format(DATE_FORMAT),
        to:
          this.filter?.date.to ||
          this.$moment(this.lastDate, DATE_FORMAT).format(DATE_FORMAT),
      },
      compare: {
        from: this.$moment(this.filter?.date.from, DATE_FORMAT)
          .subtract(1, "years")
          .format(DATE_FORMAT),
        to: this.$moment(this.filter?.date.to, DATE_FORMAT)
          .subtract(1, "years")
          .format(DATE_FORMAT),
      },
    };
  }

  get comparatorChartFilter(): ComparatorChartFilter {
    const start = this.$moment(this.filter?.date.from);
    const end = this.$moment(this.filter?.date.to);
    const days = end.diff(start, "days") + 1;

    let granularity = "day";

    if (days > 31) {
      granularity = "month";
    } else if (days > 7) {
      granularity = "sweek";
    }

    return {
      media: {
        radio: this.userMedia.radio,
        tv: this.userMedia.tv,
      },
      date: {
        from:
          this.filter?.date.from ||
          this.$moment(this.lastDate, DATE_FORMAT)
            .subtract(6, "days")
            .format(DATE_FORMAT),
        to:
          this.filter?.date.to ||
          this.$moment(this.lastDate, DATE_FORMAT).format(DATE_FORMAT),
      },
      granularity: granularity,
      group: this.filter?.group?.id || undefined,
    };
  }

  isTopListOver(item: Company) {
    if (this.topListOverItem == null) {
      return false;
    }

    return this.topListOverItem.id == item.id;
  }

  hasFeature(feature: string) {
    if (!this.user) {
      return false;
    }

    return this.user.hasFeature(feature);
  }

  onTopListOver(item: Company | undefined) {
    this.topListOverItem = item;
  }

  onDateSelected(data: DatePickerData) {
    if (data.range && this.filter) {
      this.filter.date.from = data.range.from as string;
      this.filter.date.to = data.range.to as string;
    }
  }

  onGroupReady() {
    this.groupReady = true;
  }

  openTargetSelector() {
    if (this.targetSelector) {
      this.targetSelector?.$el.querySelector("input")?.click();
    }
  }
}
